import { useMemo, useState, type UIEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { calendarProviderToProviderId, fetchConnectors, type ConnectorsStatus } from '../../../../lib/connectors';
import { queryClient, queryKeys } from '../../../../lib/query-client';
import { useConnectConnector } from '../../../../lib/use-connect-connector';
import { connectHrefFor } from '../../../../lib/suggested-task-connect';
import { openConnectWalk } from '../../../../lib/connect-walk-popup';
import { useConnectWalkPopupEnabled } from '../../../../lib/connect-walk-popup-flag';
import { useTranslation } from '../../../../lib/i18n';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { ENTITY_TYPE } from '../../../../lib/use-generic-bi';
import {
  BAND_CONNECT_POLL_INTERVAL_MS,
  bandConnectUrlFrom,
  bandOfferAppConnected,
  bandOfferSeenFields,
  bandOptionFields,
  connectedToolkitsOf,
  shouldPollBandConnect,
  type BandOfferCardData,
  type BandPick,
} from '../../../../lib/band-cards';
import { Button, Card, ScrollArea, Typography } from '../../ui';
import { BandAgentRows, BandAppChips } from './BandAgentRows';
import styles from './BandOfferCard.module.css';

/**
 * Two whole teams to choose between, rendered ABOVE an assistant reply carrying
 * `metadata.bandOfferCard` — a sibling of `OpportunityCard` and `PostConnectedConnectorCard`, riding
 * its reply the same way.
 *
 * <h2>Tapping a panel IS the pick</h2>
 *
 * This card used to be inert, on the reasoning that the row's `choices` chips did the choosing. **The
 * harness mints no choices for a band offer** — the agent asks "which band do you want" in prose — so
 * that left two panels describing a decision with no way to take it, and the only way through was to
 * type the band's name. A tap now sends exactly that name as an ordinary user turn (`onPick`), so the
 * card is the affordance and the pick is still expressed one way on the wire.
 *
 * Each app's checkmark is read LIVE from the connectors list, so the card ticks the apps off while the walk
 * the pick opened connects them. The stored card is never rewritten for that: its metadata is part of the
 * model's history, and changing it would break the prompt cache.
 *
 * <h2>The pick is the card's button</h2>
 *
 * "Set Up & Connect" says in words what a tap does: it picks the band AND opens its connect walk.
 *
 * <h2>Side by side when there is room, a swipe carousel when there is not</h2>
 *
 * Decided by the CARD's own width (a container query), not the viewport: this card lives in a ~370px dock,
 * a floating panel or a full-screen column. Narrow, it is the phones' layout — 280px cards in a snapping
 * horizontal row under a "1 of 2" counter.
 */
/** Mirrors `.options`' gap in the stylesheet — the carousel's step is a card plus this. */
const OPTION_GAP = 12;

/**
 * "Connect All" only when the tap really opens a walk. The harness leaves the link off when every app is
 * already connected or the chain could not be armed, and then the tap only picks the band.
 */
const cachedConnectors = () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [];
const refreshConnectors = () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors });

const selectLabelKey = (option: BandOfferCardData['options'][number]) => (option.connectUrl ? 'band.offerCard.select' : 'band.offerCard.selectOnly');

export function BandOfferCard({
  card,
  conversationId,
  onPick,
}: {
  card: BandOfferCardData;
  /** The turn's conversation, stamped as the impression's entity — the join between "this band was
   *  offered" and everything else that happened in the conversation that offered it. */
  conversationId?: string;
  /** Returns whether the pick was taken. The band's connect walk opens only when it was. */
  onPick?: (option: BandPick) => boolean;
}) {
  const { t } = useTranslation();
  // Set when a pick opens the band's connect walk, which arms the connectors poll below.
  const [walkStartedAt, setWalkStartedAt] = useState<number | null>(null);
  const walkInPopup = useConnectWalkPopupEnabled();
  const seenRef = useItemSeenBi('band_offer_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: bandOfferSeenFields(card),
  });

  /**
   * Choosing a band sends the pick AND opens its connect walk, both from this one click.
   *
   * The open has to happen HERE rather than when the band's card comes back, because a browser only lets
   * a script open a window from inside a real gesture — and the chain's finish page closes its window when
   * it is done, which only a script-opened window is allowed to do. `noopener` is deliberately absent for the
   * same reason it is on the band card's "Connect all": it would sever the relationship that close needs,
   * and the page being opened is our own.
   *
   * **The pick goes first, and the walk opens only if it was taken.** The parent refuses a pick while a
   * turn is streaming and on a channel-locked thread, and opening ahead of that check left a reader
   * authorizing apps for a band nobody had chosen, with nothing in the thread to show they chose it. The
   * send is synchronous, so this is all still one gesture.
   *
   * An option with no link just picks the band, and its created card carries the "Connect all" instead.
   */
  const pick = (option: BandOfferCardData['options'][number]) => {
    if (!onPick?.(bandOptionFields(option))) {
      return;
    }
    if (option.connectUrl) {
      setWalkStartedAt(Date.now());
      openConnectWalk(bandConnectUrlFrom(option.connectUrl, 'web'), walkInPopup);
    }
  };

  // A chip is one app's own connect flow, taken without picking the band — connectors belong to the
  // account, not the band. Same rail as the band card's chips.
  // The same read draws each chip's checkmark live. The walk runs in another tab, so a pick that opened it
  // arms a bounded poll (and the focus refetch) until every app on the card is connected, as the band
  // card's walk does.
  const { data: connectors } = useQuery({
    queryKey: queryKeys.connectors,
    queryFn: fetchConnectors,
    refetchOnWindowFocus: walkStartedAt !== null,
    refetchInterval: (latest) => {
      if (walkStartedAt === null) return false;
      const live = connectedToolkitsOf(latest?.connectors);
      const allConnected = card.options.every((option) => option.apps.every((app) => bandOfferAppConnected(app, live)));
      if (allConnected) return false;
      return shouldPollBandConnect(walkStartedAt, Date.now()) ? BAND_CONNECT_POLL_INTERVAL_MS : false;
    },
  });
  const connectedToolkits = useMemo(() => connectedToolkitsOf(connectors?.connectors), [connectors]);
  const isConnected = (app: BandOfferCardData['options'][number]['apps'][number]) => bandOfferAppConnected(app, connectedToolkits);
  const { connect } = useConnectConnector({ getConnectors: cachedConnectors, refresh: refreshConnectors });
  const selectApp = (app: BandOfferCardData['options'][number]['apps'][number]) => {
    const row = connectors?.connectors.find((connector) => connector.toolkit.toLowerCase() === app.toolkit.toLowerCase());
    if (row && calendarProviderToProviderId(row.calendarProvider) !== 'apple') {
      connect(row);
      return;
    }
    const href = connectHrefFor(app.toolkit);
    if (href) window.open(href, '_blank');
  };

  const [position, setPosition] = useState(0);
  const total = card.options.length;
  // The counter follows the swipe. Every card is one width plus one gap, so the index is the scroll
  // offset over that step; `Math.abs` because an RTL row scrolls into negative offsets.
  const onScroll = (event: UIEvent<HTMLElement>) => {
    const row = event.currentTarget;
    const first = row.firstElementChild as HTMLElement | null;
    if (!first) return;
    const step = first.offsetWidth + OPTION_GAP;
    const offset = Math.abs(row.scrollLeft);
    // A row that can only scroll part of a card never reaches the last step, so the end of the scroll
    // counts as the last card.
    const max = row.scrollWidth - row.clientWidth;
    const atEnd = max > 0 && offset >= max - 1;
    setPosition(atEnd ? total - 1 : Math.min(total - 1, Math.round(offset / step)));
  };

  return (
    <section ref={seenRef} className={styles.card} aria-label={t('band.offerCard.accessibilityLabel')} data-testid="chat-band-offer-card">
      {total > 1 && (
        // Only drawn by the narrow layout (see the container query); decorative, because the cards
        // themselves are all in the accessibility tree whatever is scrolled into view.
        <Typography variant="h4" className={styles.counter} aria-hidden>
          {t('band.offerCard.position', { index: position + 1, total })}
        </Typography>
      )}
      <ScrollArea axis="horizontal" bar="hidden" className={styles.options} onScroll={onScroll}>
        {card.options.map((option) => (
          // A white card on the chat's stone surface — the DS `sunken` skin, which is the default.
          <Card key={option.id} component="article" className={styles.option} data-band={option.id}>
            <div className={styles.head}>
              <Typography variant="h3" className={styles.name}>
                {option.name}
              </Typography>
              {/* One line under the name. The design's "Goal block": the outcome this band is a bet on,
                  which is what the two cards actually differ on. An offer made before goals shipped has
                  none, and falls back to the tagline so the line is never empty. */}
              {(option.goal || option.tagline) && (
                <Typography variant="p2" className={styles.goal}>
                  {option.goal || option.tagline}
                </Typography>
              )}
            </div>
            <div className={styles.section} role="group" aria-label={t('band.offerCard.team')}>
              <Typography variant="p3" component="h4" className={styles.sectionLabel}>
                {t('band.offerCard.teamLabel')}
              </Typography>
              <BandAgentRows agents={option.agents} />
            </div>
            {/* Pinned to the foot, so two cards of one height line their connectors and buttons up. */}
            <div className={styles.foot}>
              {option.apps.length > 0 && (
                <div className={styles.section}>
                  <Typography variant="p3" component="h4" className={styles.sectionLabel}>
                    {t('band.offerCard.apps')}
                  </Typography>
                  {/* NAMED chips, wrapping. A bare plate cluster asked the owner to weigh two bands on
                      apps it never named. */}
                  <BandAppChips apps={option.apps} ariaLabel={t('band.offerCard.apps')} isConnected={isConnected} onSelectApp={selectApp} />
                </div>
              )}
              {/* Only when a pick is possible: on a channel-locked thread a button that cannot
                  answer is a dead affordance. The label names the band to a screen reader, since two
                  identical buttons would otherwise be indistinguishable. */}
              {onPick && (
                <Button
                  size="small"
                  className={styles.select}
                  aria-label={`${t(selectLabelKey(option))}: ${option.name}`}
                  onClick={() => pick(option)}
                >
                  {t(selectLabelKey(option))}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </ScrollArea>
    </section>
  );
}
